import * as React from "react";
import {
  FieldPath,
  SubmitHandler,
  UseFormProps,
  useForm,
  FormProvider,
} from "react-hook-form";
import type { FieldValues } from "react-hook-form";
import { Button } from "../../components/ui/button";
import { LoadingIcon } from "../../components/system/loading-icon";
import { InputHook } from "../../components/system/input-hook";

type Props<TFormShape extends FieldValues> = {
  formActive: boolean;
  submitting: boolean;
  error: boolean;
  errorMessage: string;
  formOptions: UseFormProps<TFormShape>;
  fieldName: FieldPath<TFormShape>;
  value: string;
  inputType?: string;
  handleCancel: () => void;
  handleSubmit: SubmitHandler<TFormShape>;
  className?: string;
};

export const SingleLineForm = <TFormShape extends Record<string, unknown>>({
  formActive,
  submitting,
  error,
  errorMessage = "",
  formOptions,
  fieldName,
  value,
  inputType = "text",
  handleCancel,
  handleSubmit,
  className,
}: Props<TFormShape>): React.ReactElement => {
  const methods = useForm<TFormShape>({ ...formOptions, mode: "onChange" });
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  const formRef = React.useRef<HTMLFormElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Sync form with the value prop when it changes
  React.useEffect(() => {
    methods.reset({
      ...formOptions.defaultValues,
      [fieldName]: value,
    } as TFormShape);
  }, [value, fieldName, methods, formOptions.defaultValues]);

  const handleCancelButtonClick = () => {
    // Reset the form to the current value prop
    methods.reset({
      ...formOptions.defaultValues,
      [fieldName]: value,
    } as TFormShape);
    setIsFocused(false);
    setHasChanges(false);
    handleCancel();
  };

  const handleSubmitSuccess = () => {
    setIsFocused(false);
    setHasChanges(false);
  };

  // Watch for changes in the form
  React.useEffect(() => {
    const subscription = methods.watch((formValue, { name }) => {
      if (name === fieldName) {
        const currentValue = formValue[name as keyof typeof formValue];
        setHasChanges(currentValue !== value);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, fieldName, value]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (e: FocusEvent) => {
      // Only set focused if the target is within this specific container
      if (container.contains(e.target as Node)) {
        setIsFocused(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      // Only unfocus if we're leaving this specific container
      if (container && !container.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
      }
    };

    container.addEventListener("focusin", handleFocusIn, true);
    container.addEventListener("focusout", handleFocusOut, true);

    return () => {
      container.removeEventListener("focusin", handleFocusIn, true);
      container.removeEventListener("focusout", handleFocusOut, true);
    };
  });

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit((data) => {
          handleSubmit(data);
          handleSubmitSuccess();
        })}
        className={className}
      >
        <div ref={containerRef} className="flex flex-col gap-2">
          <div className="flex flex-col">
            <InputHook
              name={fieldName as string}
              type={inputType}
              disabled={false}
              required
            />
            {errorMessage && (
              <span className="text-sm text-destructive">{errorMessage}</span>
            )}
          </div>
          <div
            className={
              "flex flex-row gap-2 " + (isFocused || hasChanges ? "" : "hidden")
            }
          >
            <Button
              onClick={handleCancelButtonClick}
              disabled={false}
              size="default"
              variant="outline"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} size="default">
              {submitting ? <LoadingIcon /> : "Submit"}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
